import useSlotGames, { FindSlotGamesSort } from "hooks/slots/useSlotGames";
import React, { useMemo } from "react";
import { ChallengeFilterBadge, ComponentTitle, ComponentTitleBlock, SlotGrid } from "./elements";
import Arrow from "/public/images/icons/arrow-left.svg";
import Empty from "/public/images/icons/empty.svg";
import RevealSection from "components/common/RevealSection";
import Skeleton from "components/common/Skeleton";

import CherrySvg from "/public/images/NewAllGames/icons/cherry.svg";
import SlotGameCardNew from "./SlotGameCardNew";
import WrewardsButton from "components/wrewards-ui-kit/Button";
import { CircularProgress } from "@chakra-ui/react";
import { usePrevNextButtons } from "components/leaderboard/SlideButtonGroup";
import useEmblaCarousel from "embla-carousel-react";
import styled from "styled-components";
import { Flame, Rocket } from "lucide-react";
import { Target } from "layout/HeaderComponent/components/icons";

type Props = {
  title?: string;
  isNewReleases?: boolean;
  isFavorite?: boolean;
  isHot?: boolean;
  isGrid?: boolean;
  isChallenge?: boolean;
  provider?: string;
  challengeParams?: null | {
    id: string;
    title: string;
  };
  removeChallengeFilter?: () => void;
};
const take = 20;
const EAGER_CARDS_COUNT = 8;

function PopularSlots({
  title = "New Releases",
  isNewReleases,
  isFavorite,
  isHot,
  isChallenge,
  isGrid = false,
  provider,
  challengeParams,
  removeChallengeFilter,
}: Props) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: "center" });
  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } =
    usePrevNextButtons(emblaApi);

  const {
    data: { data: slots },
    isFetching,
    isFetched,
    refetch,
  } = useSlotGames({
    skip: 0,
    take,
    sort: isNewReleases ? FindSlotGamesSort.NEW_RELEASES : FindSlotGamesSort.MOST_POPULAR,
    provider,
    isFavorite,
    isHot,
    isChallenge,
    challengeId: isChallenge ? challengeParams?.id : undefined,
  });

  const isReady = isFetched;

  const refetchAction = useMemo(() => {
    if (isFavorite) return refetch;
    else return () => {};
  }, [isFavorite, refetch]);

  const iconByType = useMemo(() => {
    if (isChallenge) return <Target />;
    if (isHot) return <Flame />;
    if (isNewReleases) return <Rocket />;
    return <CherrySvg />;
  }, [isNewReleases, isHot, isChallenge]);

  return (
    <div>
      <ComponentTitleBlock>
        <ComponentTitle>
          {iconByType}
          {title}
          {challengeParams?.title && removeChallengeFilter && (
            <ChallengeFilterBadge>
              Challenge: {challengeParams.title}
              <button type="button" onClick={removeChallengeFilter}>
                ×
              </button>
            </ChallengeFilterBadge>
          )}
        </ComponentTitle>
        {!isGrid && (
          <div style={{ display: "flex", gap: "16px" }}>
            <div style={{ display: "flex", gap: "8px" }}>
              <WrewardsButton
                variant="primary-icon"
                disabled={prevBtnDisabled}
                onClick={() => onPrevButtonClick()}
              >
                <Arrow />
              </WrewardsButton>
              <WrewardsButton
                variant="primary-icon"
                disabled={nextBtnDisabled}
                onClick={() => onNextButtonClick()}
              >
                <Arrow style={{ transform: "rotate(180deg)" }} />
              </WrewardsButton>
            </div>
          </div>
        )}
      </ComponentTitleBlock>
      <CarouselWrapper>
        {isReady && isFetching && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              backdropFilter: "blur(6px)",
              borderRadius: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all linear 0.2s",
              minHeight: 60,
            }}
          >
            <CircularProgress isIndeterminate />
          </div>
        )}
        {isGrid ? (
          <RevealSection
            isReady={isReady}
            skeleton={
              <SlotGrid>
                {Array.from({ length: 8 }).map((_, index) => (
                  <Skeleton key={index} aspectRatio="3/4" radius="8px" />
                ))}
              </SlotGrid>
            }
          >
            {slots.length > 0 ? (
              <SlotGrid>
                {slots.map((slot, index) => {
                  return (
                    <SlotGameCardNew
                      key={slot.code}
                      code={slot.code}
                      codeSlug={slot.codeSlug}
                      imageCode={slot.imageCode}
                      imageBgUrl={slot.imageBgUrl}
                      imageIconUrl={slot.imageIconUrl}
                      imageTextUrl={slot.imageTextUrl}
                      name={slot.name}
                      provider={slot.provider}
                      isFavorite={slot.isFavorite}
                      refetchAction={refetchAction}
                      eager={index < EAGER_CARDS_COUNT}
                    />
                  );
                })}
              </SlotGrid>
            ) : (
              <>
                {isFetched ? (
                  <EmptyIconContainer data-grid={true}>
                    <EmptyIcon />
                    No records found
                  </EmptyIconContainer>
                ) : null}
              </>
            )}
          </RevealSection>
        ) : (
          <RevealSection
            isReady={isReady}
            skeleton={
              <CarouselSkeletonTrack>
                {Array.from({ length: 8 }).map((_, index) => (
                  <CarouselSkeletonSlide key={index}>
                    <Skeleton aspectRatio="3/4" radius="8px" />
                  </CarouselSkeletonSlide>
                ))}
              </CarouselSkeletonTrack>
            }
          >
            <div className="buttonSlider__viewport" ref={emblaRef}>
              <div className="buttonSlider__container">
                {slots.length > 0 ? (
                  slots.map((slot, index) => {
                    return (
                      <SlotGameCardNew
                        className="buttonSlider__slide"
                        key={slot.code}
                        name={slot.name}
                        provider={slot.provider}
                        code={slot.code}
                        codeSlug={slot.codeSlug}
                        imageCode={slot.imageCode}
                        imageBgUrl={slot.imageBgUrl}
                        imageIconUrl={slot.imageIconUrl}
                        imageTextUrl={slot.imageTextUrl}
                        isFavorite={slot.isFavorite}
                        eager={index < EAGER_CARDS_COUNT}
                      />
                    );
                  })
                ) : (
                  <>
                    {isFetched ? (
                      <EmptyIconContainer>
                        <EmptyIcon />
                        No records found
                      </EmptyIconContainer>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </RevealSection>
        )}
      </CarouselWrapper>
    </div>
  );
}

const EmptyIcon = styled(Empty)`
  height: 50px;
`;

const EmptyIconContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  justify-content: center;
  height: 170px;
  font-family: var(--font-family-golos);
  color: var(--clouds-100, #abbadb);
  width: 100%;

  &[data-grid="true"] {
    height: 200px;
  }
`;

const CarouselWrapper = styled.div`
  --slide-spacing: 1rem;
  --slide-size: 170px;
  width: 100%;
  flex: 1;
  width: 100%;
  position: relative;
  @media (max-width: 940px) {
    max-width: 100%;
  }

  .buttonSlider__viewport {
    overflow: hidden;
    position: relative;
    z-index: 1;
    width: 100%;
  }
  .buttonSlider__container {
    width: 100%;
    display: flex;
    gap: 16px;
    touch-action: pan-y pinch-zoom;
  }
  .buttonSlider__slide {
    transform: translate3d(0, 0, 0);
    flex: 0 0 var(--slide-size);
    min-width: 100px;
    position: relative;
    z-index: 10;
  }
`;

const CarouselSkeletonTrack = styled.div`
  display: flex;
  gap: 16px;
  overflow: hidden;
  width: 100%;
`;

const CarouselSkeletonSlide = styled.div`
  flex: 0 0 var(--slide-size);
`;

export default PopularSlots;
